import { useId, type ReactNode } from "react";
import { Box, Divider, Rating, Switch, Typography } from "@mui/material";

const MAX_STARS = 5;

// Mirrors src/styles/_variables.scss — MUI's sx cannot read the SCSS tokens.
const BORDER = "#e2e8f0";
const PANEL_BG = "#f8fafc";
const LABEL = "#334155";
const HELPER = "#64748b";
const SECTION_LABEL = "#94a3b8";
const STAR_FILLED = "#ffb300";
const STAR_EMPTY = "#cbd5e1";

/**
 * A labelled row. The control is rendered from the row's own label id so the
 * visible text is what a screen reader announces, which a bare Typography
 * beside an input would not give.
 */
const SettingRow = ({
  label,
  helper,
  children,
}: {
  label: string;
  helper: string;
  children: (labelId: string) => ReactNode;
}) => {
  const labelId = useId();

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        px: 2.25,
        py: 1.5,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography
          id={labelId}
          sx={{ fontSize: 14, fontWeight: 600, color: LABEL, lineHeight: 1.35 }}
        >
          {label}
        </Typography>
        <Typography sx={{ fontSize: 12, color: HELPER, mt: 0.25 }}>
          {helper}
        </Typography>
      </Box>
      <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center" }}>
        {children(labelId)}
      </Box>
    </Box>
  );
};

type Props = {
  /** Omit both isPaid props to hide the row — videos have no paywall toggle. */
  isPaid?: boolean;
  onIsPaidChange?: (value: boolean) => void;
  sendNotification: boolean;
  onSendNotificationChange: (value: boolean) => void;
  rating: number | null;
  onRatingChange: (value: number | null) => void;
};

/**
 * The three settings that decide how a post goes live, grouped into one panel
 * so they read as a set rather than as loose controls between the editors.
 */
const PostPublishSettings = ({
  isPaid,
  onIsPaidChange,
  sendNotification,
  onSendNotificationChange,
  rating,
  onRatingChange,
}: Props) => (
  <Box
    sx={{
      border: `1px solid ${BORDER}`,
      borderRadius: "12px",
      bgcolor: PANEL_BG,
      overflow: "hidden",
    }}
  >
    <Typography
      sx={{
        px: 2.25,
        pt: 1.75,
        pb: 1.25,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: SECTION_LABEL,
      }}
    >
      Publish Settings
    </Typography>
    <Divider sx={{ borderColor: BORDER }} />

    {onIsPaidChange && (
      <>
        <SettingRow
          label="Is Paid"
          helper="Only Insider subscribers can read this"
        >
          {(labelId) => (
            <Switch
              checked={isPaid ?? false}
              onChange={(e) => onIsPaidChange(e.target.checked)}
              name="isPaid"
              color="primary"
              inputProps={{ "aria-labelledby": labelId }}
            />
          )}
        </SettingRow>
        <Divider sx={{ borderColor: BORDER }} />
      </>
    )}

    <SettingRow
      label="Notify Users on Publish"
      helper="Send a push notification when this goes live"
    >
      {(labelId) => (
        <Switch
          checked={sendNotification}
          onChange={(e) => onSendNotificationChange(e.target.checked)}
          name="sendNotification"
          color="primary"
          inputProps={{ "aria-labelledby": labelId }}
        />
      )}
    </SettingRow>
    <Divider sx={{ borderColor: BORDER }} />

    <SettingRow
      label="Star Rating"
      helper={rating ? `${rating} of ${MAX_STARS} stars` : "Optional"}
    >
      {(labelId) => (
        <Rating
          name="rating"
          value={rating}
          max={MAX_STARS}
          onChange={(_, value) => onRatingChange(value)}
          aria-labelledby={labelId}
          sx={{
            "& .MuiRating-iconFilled": { color: STAR_FILLED },
            "& .MuiRating-iconEmpty": { color: STAR_EMPTY },
          }}
        />
      )}
    </SettingRow>
  </Box>
);

export default PostPublishSettings;
